import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  checkAndClearCacheIfNeeded, 
  clearTimezoneCache, 
  wasRecentlyCacheCleared 
} from '@/utils/cacheManager';
import { useToast } from '@/hooks/use-toast';

interface CacheManagerProps {
  // Force cache check on critical pages like booking flows
  forceCheck?: boolean;
  // Show user notification when cache is cleared
  showNotification?: boolean;
}

export const CacheManager = ({ 
  forceCheck = false, 
  showNotification = false 
}: CacheManagerProps) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    const performCacheCheck = () => {
      try {
        // Clear timezone-specific cache data
        clearTimezoneCache();
        
        // Check if app version has changed
        const cacheCleared = checkAndClearCacheIfNeeded({
          forceRefresh: forceCheck,
          reloadPage: false // Don't auto-reload, let user control
        });

        if (cacheCleared) {
          // Clear React Query cache
          queryClient.clear();
          
          console.log('Cache cleared due to version update or timezone issues');
          
          if (showNotification && !wasRecentlyCacheCleared()) {
            toast({
              title: "App Updated",
              description: "We've cleared old data to ensure you have the latest features and fixes.",
              duration: 5000,
            });
          }
        }
      } catch (error) {
        console.error('Error in cache management:', error);
      }
    };

    performCacheCheck();
  }, [forceCheck, showNotification, queryClient, toast]);

  // This component doesn't render anything
  return null;
};

export default CacheManager;